import React, { useState, memo, useEffect } from 'react'
import { reduceBy, reject, omit, pipe, filter } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { partyApiEvents, partyApiSelectors } from 'modules/api/party'
import { userApiSelectors } from 'modules/api/user'
import Button from '../../atoms/Button/Button'
import Select from '../../atoms/Select/Select'
import ClearRed from '../../atoms/Svgs/ClearRed'
import EasyPayBeneficiary from './EasyPayBeneficiary/EasyPayBeneficiary'
import EftBeneficiary from './EftBeneficiary/EftBeneficiary'
import NoAttachedLeaseBeneficiary from './NoAttachedLeaseBeneficiary/NoAttachedLeaseBeneficiary'
import styles from './NewBeneficiary.module.scss'

const NO_ATTACHED_LEASE = 'No Attached Lease'

interface INewBeneficiaryProps {
  onNewBeneficiaryAdded: (beneficiary: any) => void
  onCancel?: () => void
  tags?: string
  getDefaultResultSets?: (partyTag: string) => any[]
  partyTags: { value: string; label: string }[]
  fetchEasyPayData: (reference: string) => void
  easyPayData: any
  portfolioId: string
  primaryOwnerPartyId: string
  propertyOwnerName: string
  unallocatedAmount: number
}

const NewBeneficiary = ({
  onNewBeneficiaryAdded,
  onCancel = () => {},
  tags = '',
  getDefaultResultSets,
  partyTags,
  fetchEasyPayData,
  easyPayData,
  portfolioId,
  primaryOwnerPartyId,
  propertyOwnerName,
  unallocatedAmount,
}: INewBeneficiaryProps) => {
  const dispatch = useDispatch()

  const [selection, setSelection] = useState(null)

  const defaultEasyPayState = {
    easyPayReference: '',
    beneficiaryTag: '',
    amount: 0,
  }
  const [easyPayState, setEasyPayState] = useState(defaultEasyPayState)

  // Reset easyPayState when selection changes to EasyPay
  useEffect(() => {
    if (selection === 'EasyPay') {
      setEasyPayState(defaultEasyPayState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection])

  /**
   * Fetch agency party if not in state, used to auto select agency on subsequent step.
   * Also fetch on invoice and portfolio epics, but adding as a precaution to ensure the agency party exists in state.
   */
  const currentAgencyId = useSelector(userApiSelectors.getCurrentAgencyId)
  const agencyParty = useSelector(partyApiSelectors.getAgencyParty)
  useEffect(() => {
    if (!agencyParty && currentAgencyId) {
      dispatch(partyApiEvents.party_request(currentAgencyId))
    }
  }, [dispatch, currentAgencyId, agencyParty])

  const defaultEftState = {
    partyId: '',
    partyTag: '',
    name: '',
    reference: '',
    amount: 0,
  }
  const [eftState, setEftState] = useState(defaultEftState)

  // Reset eftState when selection changes to ensure clean state
  useEffect(() => {
    if (selection && selection !== 'EasyPay' && selection !== NO_ATTACHED_LEASE) {
      setEftState({
        ...defaultEftState,
        partyTag: selection,
      })
    }
    // Re-create defaultEftState reference on each call to ensure fresh object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection])

  const defaultNoAttachedLeaseState = {
    reference: '',
    amount: 0,
  }
  const [noAttachedLeaseState, setNoAttachedLeaseState] = useState(defaultNoAttachedLeaseState)

  useEffect(() => {
    if (selection === NO_ATTACHED_LEASE) {
      setNoAttachedLeaseState(defaultNoAttachedLeaseState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection])

  const groupedPartyTags = () => {
    const groupTags = (acc: any, tag: any) => acc.concat(tag)
    const toGroup = ({ label }: any) => label.split(/:(.+)/)[0]
    return {
      EasyPay: partyTags,
      ...reduceBy(groupTags, [], toGroup, partyTags),
    }
  }

  const groupedPartyTagsForSelect = useSelector(partyApiSelectors.getGroupedPartyTagsForSelect)

  // The selector is derived from all party tags in Redux, but callers may pass a filtered
  // `partyTags` prop (e.g. excluding deposit accounts). Drop any options that don't have
  // a matching group in the props-derived map, otherwise selection crashes downstream.
  const groups = groupedPartyTags()
  const selectOptions = (
    portfolioId
      ? [...groupedPartyTagsForSelect, { value: NO_ATTACHED_LEASE, label: NO_ATTACHED_LEASE }]
      : groupedPartyTagsForSelect
  ).filter(({ value }: { value: string }) => value === NO_ATTACHED_LEASE || value in groups)

  const handleCancel = (): void => {
    setSelection(null)
    setEasyPayState(defaultEasyPayState)
    setEftState(defaultEftState)
    setNoAttachedLeaseState(defaultNoAttachedLeaseState)
    onCancel()
  }

  return (
    <div className={styles.root}>
      <div className={styles.form}>
        <Button link size="sm" onClick={handleCancel}>
          <ClearRed width="26px" height="24px" />
        </Button>

        <div className={styles.input}>
          {!selection ? (
            <Select
              isSearchable
              onChange={(val: any) => setSelection(val.value)}
              name="selection"
              label="Beneficiary type"
              placeholder="Select"
              options={selectOptions}
            />
          ) : selection === 'EasyPay' ? (
            <EasyPayBeneficiary
              key={`easypay-${selection}`}
              beneficiaryTagGroups={omit(['EasyPay'], groupedPartyTags())}
              beneficiaryTagGroupsForSelect={pipe(
                reject(({ value }: any) => value === 'EasyPay'),
                filter(({ value }: any) =>
                  ['Body Corporate', 'Contractor', 'Levies', 'Municipality', 'Rates', 'Supplier'].includes(value),
                ),
              )(groupedPartyTagsForSelect)}
              onNewBeneficiaryAdded={onNewBeneficiaryAdded}
              state={easyPayState}
              setState={val => setEasyPayState({ ...easyPayState, ...val })}
              easyPayData={easyPayData}
              fetchEasyPayData={fetchEasyPayData}
              unallocatedAmount={unallocatedAmount}
            />
          ) : selection === NO_ATTACHED_LEASE && portfolioId ? (
            <NoAttachedLeaseBeneficiary
              key={`no-attached-lease-${selection}`}
              primaryOwnerPartyId={primaryOwnerPartyId}
              onNewBeneficiaryAdded={onNewBeneficiaryAdded}
              state={noAttachedLeaseState}
              setState={val => setNoAttachedLeaseState({ ...noAttachedLeaseState, ...val })}
              unallocatedAmount={unallocatedAmount}
            />
          ) : (
            <EftBeneficiary
              key={`eft-${selection}`}
              partyTags={groupedPartyTags()[selection]}
              onNewBeneficiaryAdded={onNewBeneficiaryAdded}
              state={eftState}
              setState={val => setEftState({ ...eftState, ...val })}
              getDefaultResultSets={getDefaultResultSets}
              tags={tags}
              propertyOwnerName={propertyOwnerName}
              unallocatedAmount={unallocatedAmount}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(NewBeneficiary)
